import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

/** DFS order with the folders above each category, so three categories called
 *  "Portal" can be told apart, and a parent missing from this tree (hidden by
 *  visibility) never swallows its children. */
function flatten(tree) {
  const cats = tree || [];
  const ids = new Set(cats.map((c) => c.cat_id));
  const byParent = {};
  cats.forEach((c) => {
    const key = c.parent_id && ids.has(c.parent_id) ? c.parent_id : "root";
    (byParent[key] ||= []).push(c);
  });
  Object.values(byParent).forEach((a) => a.sort((x, y) => (x.position || 0) - (y.position || 0)));
  const out = [];
  const walk = (pid, above) => (byParent[pid] || []).forEach((c) => {
    const name = (c.name || "").trim();
    out.push({ cat_id: c.cat_id, name, above,
               search: `${above.join(" ")} ${name}`.toLowerCase() });
    walk(c.cat_id, [...above, name]);
  });
  walk("root", []);
  return out;
}

/**
 * Category picker — a flat searchable LIST (never a nested tree), styled to sit
 * flush with the Select filters beside it. Multi-select by default; `single`
 * keeps the one-home-category behaviour the editors need, and `variant="pill"`
 * matches the reader's search bar.
 */
export function KbCategoryFilter({
  tree, value, onChange, single = false, variant = "filter",
  allLabel = "All Categories", exclude = [], testid = "kb-category-filter",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const rows = useMemo(
    () => flatten(tree).filter((r) => !exclude.includes(r.cat_id)),
    [tree, exclude]);
  const picked = single ? (value && value !== "all" ? [value] : []) : (value || []);
  const chosen = new Set(picked);
  const label = picked.length === 0 ? allLabel
    : picked.length === 1 ? (rows.find((r) => r.cat_id === picked[0])?.name || "1 category")
      : `${picked.length} categories`;

  const q = query.trim().toLowerCase();
  const opts = q ? rows.filter((r) => r.search.includes(q)) : rows;

  const toggle = (id) => {
    if (single) { onChange(id); setOpen(false); setQuery(""); return; }
    onChange(chosen.has(id) ? picked.filter((p) => p !== id) : [...picked, id]);
  };
  const clear = () => {
    onChange(single ? "all" : []);
    if (single) setOpen(false);
  };

  const trigger = variant === "pill"
    ? "flex h-12 w-full items-center justify-between gap-2 rounded-full border-0 bg-white dark:bg-slate-900 pl-4 pr-3 text-sm text-slate-700 dark:text-slate-200 shadow-lg sm:w-56"
    : "flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring";

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button type="button" className={trigger} title={label} data-testid={testid}>
          <span className={`truncate ${picked.length ? "" : "text-muted-foreground"}`}>{label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[22rem] p-0" data-testid={`${testid}-options`}>
        <div className="relative border-b border-slate-100 dark:border-slate-800 p-2">
          <Search className="absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input autoFocus value={query} placeholder="Search categories…"
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 pl-7 text-xs" data-testid={`${testid}-search`} />
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          <button type="button" onClick={clear} data-testid={`${testid}-opt-all`}
            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${picked.length ? "text-slate-700 dark:text-slate-200" : "font-semibold text-primary"}`}>
            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
              {picked.length === 0 && <Check className="h-3.5 w-3.5" />}
            </span>
            {allLabel}
          </button>
          {opts.map((r) => {
            const on = chosen.has(r.cat_id);
            return (
              <button key={r.cat_id} type="button" onClick={() => toggle(r.cat_id)}
                title={[...r.above, r.name].join(" › ")} data-testid={`${testid}-opt-${r.cat_id}`}
                className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800">
                <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? "border-primary bg-primary text-primary-foreground" : "border-slate-300 dark:border-slate-600"}`}>
                  {on && <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0 flex-1" style={{ paddingLeft: `${Math.min(r.above.length, 4) * 10}px` }}>
                  {r.above.length > 0 && (
                    <span className="block truncate text-[11px] text-slate-400 dark:text-slate-500">
                      {r.above.join(" › ")} ›
                    </span>
                  )}
                  <span className={`block truncate text-sm ${on ? "font-semibold text-primary" : "text-slate-700 dark:text-slate-200"}`}>
                    {r.name}
                  </span>
                </span>
              </button>
            );
          })}
          {opts.length === 0 && (
            <div className="px-2 py-3 text-xs text-slate-400 dark:text-slate-500">No matching categories</div>
          )}
        </div>
        {!single && picked.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 px-3 py-1.5 text-xs">
            <span className="text-slate-500 dark:text-slate-400">{picked.length} selected</span>
            <button type="button" onClick={clear} data-testid={`${testid}-clear`}
              className="font-medium text-primary hover:underline">Clear</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
